<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="4" />
      <vs-col
        vs-w="7" 
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-2">
          <div class="flex items-center w-full">
            <select-with-search
              v-model="filters.type"
              placeholder="Select report"
              :options="reportTypeOptions"
            />
          </div>
          <div class="flex items-center w-full">
            <select-with-search
              v-model="filters.company"
              placeholder="Select Company"
              :options="companiesOptions"
            />
          </div>
          <div class="flex items-center w-full">
            <date-picker
              :type="dateTypeForType()"
              :format="dateFormatForType()"
              value-type="format"
              placeholder="Select Month"
              v-model="filters.date"
            />
          </div>
          <div class="flex items-center pl-2">
            <vs-button
              type="border"
              color="primary"
              class="self-center 2xl:self-end font-semibold"
              style="min-width: 100px;"
              @click="() => {
                filters = $options.data().filters
                urlEmbed = ''
              }"
            >
              Reset
            </vs-button>
          </div>
          <div class="flex items-center">
            <vs-button
              type="filled"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 120px;"
              @click="setUrlParam"
              :disabled="!!!filters.type || !!!filters.company || !!!filters.date"
            >
              View Report
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="h-screen flex items-center">
      <template
        v-if="!urlEmbed"
      >
        <div class="w-full flex flex-col items-center gap-y-3">
          <p class="text-2xl font-semibold">
            No Data Found
          </p>
          <p
            class="text-grey-001"
          >
            Select filter to show the report data
          </p>
        </div>
      </template>
      <template v-else>
        <div class="w-full h-full flex flex-col items-center">
          <iframe
            :src="urlEmbed"
            class="border-0 relative h-full w-full"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import { mapGetters } from 'vuex'

import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import { PAGE_TITLE } from '@/fixtures/Constants'

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    SelectWithSearch,
    DatePicker
  },
  data() {
    return {
      isBusy: false,
      filters: {
        type: null,
        company: null,
        date: moment().format('MMM YYYY')
      },
      companiesOptions: [],
      reportTypeOptions: [
        {
          text: 'Utilization & Hiring Cost',
          value: 'HIRING',
          type: 'HIRING'
        },
        {
          text: 'Utilization & Freight Cost',
          value: 'FREIGHT',
          type: 'FREIGHT'
        },
        {
          text: 'Fuel Cost',
          value: 'FUEL',
          type: 'FUEL'
        },
        {
          text: 'Government & General Expenses',
          value: 'GOV_EXPENSES',
          type: 'GOV_EXPENSES'
        },
        {
          text: 'Measure Per Set',
          value: 'MEASUREMENT',
          type: 'MEASUREMENT'
        },
        {
          text: 'Delay Report',
          value: 'DELAY',
          type: 'DELAY'
        },
        {
          text: 'QCTSE Measure',
          value: 'QCTSE_MEASURE',
          type: 'QCTSE_MEASURE'
        }
      ],
      urlEmbed: ''
    }
  },
  computed: {
    ...mapGetters({
      companies: 'Companies/companies'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    }
  },
  watch: {
    companies: {
      handler(val) {
        if (val) {
          this.companiesOptions = val.map(item => ({
            text: item.code,
            value: item.code
          }));
        }
      }
    }
  },
  mounted() {
    this.fetchCompany()
  },
  methods: {
    async fetchCompany() {
      try {
        await this.$store.dispatch('Companies/fetchCompanies', {
          is_master_data: true,
          type: 'SITE_OWNER',
          status: true,
          sort_dir: 'asc',
          sort_by: 'name',
          limit: null,
          page: null
        })
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch companies, please try again')
      }
    },
    setUrlParam() {
      if (this.filters.type === 'DELAY') {
        const url = `${process.env.VUE_APP_SSRS_URL}/DELAY_REPORT`;
        const params = {
          company: this.filters.company,
          month_year: moment(this.filters.date, 'MMM YYYY').format('MM-YYYY')
        }
        this.urlEmbed = `${url}&${ new URLSearchParams(params).toString() }`;
      } else if (this.filters.type === 'QCTSE_MEASURE') {
        const url = `${process.env.VUE_APP_SSRS_URL}/QCTSE_MEASURE`;
        const params = {
          company: this.filters.company,
          year: moment(this.filters.date, 'MMM YYYY').format('YYYY')
        }
        this.urlEmbed = `${url}&${ new URLSearchParams(params).toString() }`;
      } else {
        const url = `${process.env.VUE_APP_SSRS_URL}/COST_REPORT`;
        const params = {
          ...this.filters,
          date: moment(this.filters.date, 'MMM YYYY').format('DD/MM/YYYY')
        }
        this.urlEmbed = `${url}&${ new URLSearchParams(params).toString() }`;
      }
    },
    dateFormatForType() {
      if (this.filters.type === 'QCTSE_MEASURE') {
        return 'YYYY'
      } else {
        return 'MMM YYYY'
      }
    },
    dateTypeForType() {
      if (this.filters.type === 'QCTSE_MEASURE') {
        return 'year'
      } else {
        return 'month'
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply w-full p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}
</style>
